<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown me-4" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" class="icon-button">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaIcon name="mso-account_circle" :size="40" />
          </span>
        </VaButton>
      </template>

      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <div class="va-h6 px-4 va-text-capitalize">{{ `${t("general.hello")}, ${userName}` }}</div>
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
    <VaModal
      v-slot="{ cancel }"
      v-model="doShowImpersonateModal"
      size="small"
      mobile-fullscreen
      close-button
      hide-default-actions
      no-outside-dismiss
    >
      <h1 class="va-h5">{{ t("impersonate.title") }}</h1>
      <Impersonate @close="cancel" />
    </VaModal>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useColors } from "vuestic-ui"
import { useUserStore } from "@/stores/user-store"
import { useGlobalStore } from "@/stores/global-store"
import { checkAccess } from "@/services/authorizationPlugin"
import { Resources } from "@shared/helpers/resources"
import { PermissionAction } from "@shared/components/core/helpers/securityTypes"
import Impersonate from "@/pages/common/Impersonate.vue"
import { useRouter } from "vue-router"

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const doShowImpersonateModal = ref(false)
const router = useRouter()
const globalStore = useGlobalStore()

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action?: string
  requiredPermission?: string
  requiredAction?: PermissionAction
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const availableOptions: ProfileOptions[] = [
  {
    name: "general.account",
    separator: true,
    list: [
      {
        name: "about.title",
        to: "release-notes",
        icon: "info",
      },
      {
        name: "settings.title",
        to: "settings",
        icon: "settings",
      },
      {
        name: "userInfo.title",
        to: "user-info",
        icon: "assignment_ind",
      },
      {
        name: "impersonate.title",
        icon: "supervised_user_circle",
        action: "impersonate",
        requiredPermission: Resources.impersonate,
        requiredAction: PermissionAction.Write,
      },
    ],
  },
  {
    name: "",
    separator: false,
    list: [
      {
        name: "general.logout",
        to: "login",
        icon: "mso-logout",
        action: "logout",
      },
    ],
  },
]

const options = computed<ProfileOptions[]>(() => {
  // Get the latest permissions
  const permissions = userStore.user.permissions

  // Filter each group and its items
  return (
    availableOptions
      .map((group) => ({
        ...group,
        list: group.list.filter(
          (item) => !item.requiredPermission || checkAccess(item.requiredPermission, item.requiredAction!, permissions),
        ),
      }))
      // Optionally, filter out groups with no visible items
      .filter((group) => group.list.length > 0)
  )
})

const isShown = ref(false)
const userStore = useUserStore()
const userName = computed(() => userStore.user.firstName || "User")

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) {
    return { onClick: () => action(item.action as string) }
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: "_blank" } : {}
}

const action = (actionFunction: string) => {
  if (actionFunction === "logout") {
    logout()
  }
  if (actionFunction === "impersonate") {
    impersonate()
  }
}

const logout = async () => {
  globalStore.showAppLoading()
  await router.push({ name: "home" })
  userStore.logoutUser()
  window.location.reload()
}

const impersonate = () => {
  doShowImpersonateModal.value = true
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }
}

.icon-button span {
  padding: 0px;
}
</style>
